import React from 'react';
import {renderStringOrSupplement} from './utils';
import {extractSupplementParts} from './utils';
import {Chip, Icon} from '@albert-io/atomic';
import classNames from 'classnames';
import {
  DraggableChip,
  getSmallSupplement,
  SmallSupplementContent,
  useEnsureSupplementLoaded,
  useIsSmallSupplementsEnabled
} from './DraggableChip';

interface MovableItemProps {
  item: {id: string; value: string; placedInSlot?: string | null};
  isActive: boolean;
  activeItemId?: string | null;
  onClick: (e: React.MouseEvent) => void;
  onReset?: (itemId: string) => void;
  isDragging?: boolean;
  disabled?: boolean;
  isSubmitted?: boolean;
  isCorrect?: boolean;
  showIcon?: boolean;
  /** When true, the item never dims because another item is being dragged/selected (e.g. it's already placed in an answer). */
  suppressDimming?: boolean;
}

export const MovableItem = ({
  item,
  activeItemId,
  isActive,
  onClick,
  onReset,
  isDragging,
  isSubmitted,
  isCorrect,
  showIcon = true,
  suppressDimming
}: MovableItemProps) => {
  const isSmallSupplementsEnabled = useIsSmallSupplementsEnabled();
  // The submitted view renders supplement content directly (not through SupplementChip), so ensure
  // the supplement is loaded before resolving small-vs-full — otherwise it briefly flashes full-size.
  const isSupplementReady = useEnsureSupplementLoaded(
    item.value,
    isSmallSupplementsEnabled && !!isSubmitted
  );
  const smallSupplement = isSmallSupplementsEnabled ? getSmallSupplement(item.value) : null;
  const parts = smallSupplement ? extractSupplementParts(item.value) : null;
  const surroundingText = parts?.text || '';
  const isSubmittedSupplementPending =
    isSmallSupplementsEnabled && !isSupplementReady && !!extractSupplementParts(item.value);

  const submittedContent = smallSupplement ? (
    <>
      {surroundingText && <span className='mr-x0-half'>{surroundingText}</span>}
      <SmallSupplementContent type={smallSupplement.type} supplement={smallSupplement.supplement} />
    </>
  ) : (
    renderStringOrSupplement(item.value)
  );

  const submittedBgClass = isCorrect ? 'background-positive-subtle' : 'background-negative-subtle';

  return (
    <>
      {!isSubmitted ? (
        <DraggableChip
          id={item.id}
          value={item.value}
          isActive={isActive}
          isDragging={isDragging}
          activeItemId={activeItemId}
          onClick={onClick}
          onRemove={
            onReset
              ? (e) => {
                  e.stopPropagation();
                  if (!activeItemId) onReset(item.id);
                }
              : undefined
          }
          suppressDimming={suppressDimming}
          size='small'
        />
      ) : isSubmittedSupplementPending ? null : (
        <>
          {smallSupplement ? (
            <div
              className={classNames(
                'min-w-0 relative flex h-auto! overflow-hidden rounded-lg border border-border-primary text-left font-normal',
                submittedBgClass
              )}
            >
              {showIcon && (
                <Icon
                  icon={isCorrect ? ['far', 'check-circle'] : ['far', 'circle-xmark']}
                  className='absolute top-x1 left-x1 z-10 shrink-0 rounded-full background-primary'
                />
              )}
              {submittedContent}
            </div>
          ) : (
            <Chip
              color={isCorrect ? 'positive' : 'negative'}
              size='small'
              noMargin
              className='min-w-0 overflow-hidden border border-border-primary object-contain'
            >
              {showIcon && (
                <Icon
                  icon={isCorrect ? ['far', 'check-circle'] : ['far', 'circle-xmark']}
                  className='mr-x0-half'
                />
              )}
              <span className='truncate'>{submittedContent}</span>
            </Chip>
          )}
        </>
      )}
    </>
  );
};
